import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { 
  Shield, ChevronRight, ArrowRight, Menu, X,
  Phone, Mail, Zap, Target, BarChart3, FileCheck,
  Users, Brain, Search, Lock, MessageCircle
} from "lucide-react";
import PublicLanguageSwitcher from "../components/PublicLanguageSwitcher";

const LandingPage = () => {
  const { t } = useTranslation('platform');
  const navigate = useNavigate();
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((entry) => {
        if (entry.isIntersecting) entry.target.classList.add("visible");
      }),
      { threshold: 0.12 }
    );
    document.querySelectorAll(".reveal").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const capabilities = [
    { icon: Search, title: t('landing.capabilities.gapAssessment'), desc: t('landing.capabilities.gapAssessmentDesc'), accent: "#38BDF8" },
    { icon: FileCheck, title: t('landing.capabilities.qmsToolkit'), desc: t('landing.capabilities.qmsToolkitDesc'), accent: "#4ADE80" },
    { icon: Brain, title: t('landing.capabilities.auditIntelligence'), desc: t('landing.capabilities.auditIntelligenceDesc'), accent: "#E8A838" },
    { icon: BarChart3, title: t('landing.capabilities.marketIntelligence'), desc: t('landing.capabilities.marketIntelligenceDesc'), accent: "#A78BFA" },
    { icon: Lock, title: t('landing.capabilities.documentControl'), desc: t('landing.capabilities.documentControlDesc'), accent: "#F472B6" },
    { icon: Users, title: t('landing.capabilities.clientPortal'), desc: t('landing.capabilities.clientPortalDesc'), accent: "#FB923C" },
  ];

  const workflow = [
    { step: "01", title: t('landing.workflow.assess'), desc: t('landing.workflow.assessDesc') },
    { step: "02", title: t('landing.workflow.plan'), desc: t('landing.workflow.planDesc') },
    { step: "03", title: t('landing.workflow.execute'), desc: t('landing.workflow.executeDesc') },
    { step: "04", title: t('landing.workflow.improve'), desc: t('landing.workflow.improveDesc') },
  ];

  const sectors = [
    { title: t('landing.sectors.medicalDevices'), tags: ["ISO 13485", "FDA 820", "EU MDR"], color: "#38BDF8" },
    { title: t('landing.sectors.pharmaBiotech'), tags: ["ICH Q7", "GMP", "FDA"], color: "#4ADE80" },
    { title: t('landing.sectors.aerospace'), tags: ["AS9100", "NADCAP", "DO-178C"], color: "#FBBF24" },
    { title: t('landing.sectors.automotive'), tags: ["IATF 16949", "VDA 6.3", "AIAG"], color: "#F87171" },
    { title: t('landing.sectors.electronics'), tags: ["IEC 62443", "ISO 27001"], color: "#A78BFA" },
    { title: t('landing.sectors.labsMetrology'), tags: ["ISO 17025", "ISO 15189"], color: "#F472B6" },
  ];

  return (
    <div className="min-h-screen bg-[#111318]">
      {/* Nav */}
      <nav className={`landing-nav ${isScrolled ? "scrolled" : ""}`} data-testid="landing-nav">
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3" data-testid="logo">
            <img src="/logo.svg" alt="Charstan" className="h-10" />
          </div>
          <div className="hidden md:flex items-center gap-8">
            <a href="#capabilities" className="text-[#CBD5E1] hover:text-[#38BDF8] transition-colors text-sm font-medium">{t('landing.nav.platform')}</a>
            <a href="#workflow" className="text-[#CBD5E1] hover:text-[#38BDF8] transition-colors text-sm font-medium">{t('landing.nav.howItWorks')}</a>
            <a href="#sectors" className="text-[#CBD5E1] hover:text-[#38BDF8] transition-colors text-sm font-medium">{t('landing.nav.industries')}</a>
            <PublicLanguageSwitcher />
            <button onClick={() => navigate("/login")} className="btn-primary" data-testid="get-started-btn">
              {t('landing.nav.launchPlatform')}
            </button>
          </div>
          <button className="md:hidden text-white" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-testid="mobile-menu-btn">
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
        {mobileMenuOpen && (
          <div className="md:hidden bg-[#181B23] border-t border-[rgba(255,255,255,0.07)] px-6 py-4">
            <a href="#capabilities" className="block py-3 text-[#CBD5E1]">{t('landing.nav.platform')}</a>
            <a href="#workflow" className="block py-3 text-[#CBD5E1]">{t('landing.nav.howItWorks')}</a>
            <a href="#sectors" className="block py-3 text-[#CBD5E1]">{t('landing.nav.industries')}</a>
            <div className="py-3"><PublicLanguageSwitcher /></div>
            <button onClick={() => navigate("/login")} className="btn-primary w-full mt-4">{t('landing.nav.launchPlatform')}</button>
          </div>
        )}
      </nav>

      {/* Hero — Clean, focused */}
      <section className="hero-section" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-0">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#1F222D] rounded-full mb-6">
              <span className="pulse-dot" />
              <span className="text-[#E8A838] text-xs font-medium uppercase tracking-wider">{t('landing.hero.badge')}</span>
            </div>
            <h1 className="font-['Syne'] text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
              {t('landing.hero.line1')}<br />
              {t('landing.hero.line2')}<br />
              <span className="gradient-text">{t('landing.hero.line3')}</span>
            </h1>
            <p className="text-[#8B95A5] text-lg mb-8 max-w-xl">
              {t('landing.hero.subtitle')}
            </p>
            <div className="flex flex-wrap gap-4">
              <button onClick={() => navigate("/login")} className="btn-primary" data-testid="hero-cta-primary">
                {t('landing.hero.accessPlatform')} <ArrowRight size={18} />
              </button>
              <a href="#capabilities" className="btn-ghost">
                {t('landing.hero.seeCapabilities')} <ChevronRight size={18} />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Stats — Compact */}
      <section className="stats-strip py-10" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { value: "283+", label: t('landing.stats.auditableItems') },
              { value: "16+", label: t('landing.stats.standardsSupported') },
              { value: "6", label: t('landing.stats.industryVerticals') },
              { value: "AI", label: t('landing.stats.poweredAnalysis') },
            ].map((stat, i) => (
              <div key={i} className="text-center md:border-r border-[rgba(255,255,255,0.07)] last:border-r-0">
                <div className="font-['Syne'] text-3xl font-bold text-white mb-1">{stat.value}</div>
                <div className="text-[#8B95A5] text-sm">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Capabilities — What the platform does */}
      <section id="capabilities" className="py-20 bg-[#111318]" data-testid="capabilities-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="mb-12 reveal">
            <h2 className="font-['Syne'] text-3xl md:text-4xl font-bold text-white mb-3">
              Platform Capabilities
            </h2>
            <p className="text-[#8B95A5] max-w-xl">
              Everything you need to manage compliance — AI-powered, integrated, and role-based.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {capabilities.map((cap, i) => (
              <div key={i} className={`bg-[#1F222D] border border-[rgba(255,255,255,0.07)] rounded-[3px] p-6 hover:border-[${cap.accent}]/30 transition-all reveal reveal-delay-${(i % 3) + 1}`}
                data-testid={`capability-card-${i}`}>
                <div className="w-10 h-10 rounded-[3px] flex items-center justify-center mb-4" style={{ backgroundColor: `${cap.accent}20` }}>
                  <cap.icon className="w-5 h-5" style={{ color: cap.accent }} strokeWidth={1.5} />
                </div>
                <h3 className="font-['Syne'] text-base font-semibold text-white mb-2">{cap.title}</h3>
                <p className="text-[#8B95A5] text-sm leading-relaxed">{cap.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Workflow — How it works */}
      <section id="workflow" className="py-20 bg-[#181B23]" data-testid="workflow-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-16 items-start">
            <div className="reveal">
              <h2 className="font-['Syne'] text-3xl md:text-4xl font-bold text-white mb-3">
                {t('landing.workflow.title')}
              </h2>
              <p className="text-[#8B95A5] mb-8">
                {t('landing.workflow.subtitle')}
              </p>
              <div className="space-y-1">
                {workflow.map((step, i) => (
                  <div key={i} className="approach-step rounded-[3px]" data-testid={`workflow-step-${i}`}>
                    <div className="flex items-start gap-4">
                      <span className="font-['Syne'] text-2xl font-bold text-[#38BDF8]">{step.step}</span>
                      <div>
                        <h4 className="font-['Syne'] text-base font-semibold text-white mb-1">{step.title}</h4>
                        <p className="text-[#8B95A5] text-sm">{step.desc}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="hidden md:block reveal">
              <div className="glass-card p-8 mt-8" data-testid="platform-preview">
                <div className="flex items-center gap-2 mb-6">
                  <span className="pulse-dot" />
                  <span className="text-[#E8A838] text-xs font-medium uppercase tracking-wider">{t('landing.preview.badge')}</span>
                </div>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-3 bg-[#111318] rounded">
                    <span className="text-white text-sm">{t('landing.preview.gapAssessment')}</span>
                    <span className="text-[#4ADE80] text-xs font-medium">{t('landing.preview.aiReady')}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-[#111318] rounded">
                    <span className="text-white text-sm">{t('landing.preview.qmsToolkit')}</span>
                    <span className="text-[#4ADE80] text-xs font-medium">{t('landing.preview.integrated')}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-[#111318] rounded">
                    <span className="text-white text-sm">{t('landing.preview.auditMatrices')}</span>
                    <span className="text-[#38BDF8] text-xs font-medium">{t('landing.preview.items283')}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-[#111318] rounded">
                    <span className="text-white text-sm">{t('landing.preview.marketIntel')}</span>
                    <span className="text-[#E8A838] text-xs font-medium">{t('landing.preview.competitors10')}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-[#111318] rounded">
                    <span className="text-white text-sm">{t('landing.preview.capaTracking')}</span>
                    <span className="text-[#A78BFA] text-xs font-medium">{t('landing.preview.integrated')}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sectors */}
      <section id="sectors" className="py-20 bg-[#111318]" data-testid="sectors-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="mb-12 reveal">
            <h2 className="font-['Syne'] text-3xl md:text-4xl font-bold text-white mb-3">
              Industry Coverage
            </h2>
            <p className="text-[#8B95A5] max-w-xl">
              Sector-specific compliance knowledge across regulated industries.
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {sectors.map((sector, i) => (
              <div key={i} className={`bg-[#1F222D] border border-[rgba(255,255,255,0.07)] rounded-[3px] p-5 hover:border-[#2D313D] transition-colors reveal reveal-delay-${(i % 3) + 1}`}
                data-testid={`sector-card-${i}`}>
                <h3 className="font-['Syne'] font-semibold text-white text-sm mb-2" style={{ color: sector.color }}>{sector.title}</h3>
                <div className="flex gap-1.5 flex-wrap">
                  {sector.tags.map((tag, j) => <span key={j} className="sector-tag text-[10px]">{tag}</span>)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cta-banner py-20" data-testid="cta-section">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center reveal">
          <h2 className="font-['Syne'] text-3xl md:text-4xl font-bold text-white mb-4">
            {t('landing.cta.title')}
          </h2>
          <p className="text-[#8B95A5] mb-8">
            {t('landing.cta.subtitle')}
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <button onClick={() => navigate("/login")} className="btn-primary">
              <Zap size={18} /> {t('landing.nav.launchPlatform')}
            </button>
            <a href="mailto:info@charstan.com" className="btn-ghost" data-testid="cta-contact-us" style={{textDecoration: 'none'}}>
              <Mail size={18} /> {t('landing.cta.contactUs')}
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t border-[rgba(255,255,255,0.07)]" data-testid="footer">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div data-testid="footer-usa">
              <img src="/logo.svg" alt="Charstan" className="h-8 mb-3" />
              <p className="text-[#CBD5E1] text-sm font-medium">CHARSTAN Intelligence Platform</p>
              <p className="text-[#8B95A5] text-xs mt-1">Los Angeles, CA 91342, United States</p>
              <a href="https://wa.me/18182007213" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-[#8B95A5] hover:text-[#25D366] text-xs mt-2 transition-colors" data-testid="footer-whatsapp-us">
                <Phone size={12} /> US/CANADA, EU/OUS: +1 (818) 200-7213
                <MessageCircle size={12} className="text-[#25D366]" />
              </a>
            </div>
            <div data-testid="footer-brasil">
              <p className="text-[#CBD5E1] text-sm font-medium">CHARSTAN CONSULTORIA DE QUALIDADE LTDA</p>
              <p className="text-[#8B95A5] text-xs mt-1">SALVADOR, BA 40.150-390, Brasil</p>
              <p className="text-[#8B95A5] text-xs mt-1">CNPJ: 65.223.569/0001-61</p>
              <a href="https://wa.me/5571996866142" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-[#8B95A5] hover:text-[#25D366] text-xs mt-2 transition-colors" data-testid="footer-whatsapp-br">
                <Phone size={12} /> Brasil: (71) 996866142
                <MessageCircle size={12} className="text-[#25D366]" />
              </a>
            </div>
            <div>
              <div className="flex gap-6 mb-4">
                <a href="#capabilities" className="text-[#8B95A5] hover:text-[#38BDF8] text-sm">{t('landing.nav.platform')}</a>
                <a href="#workflow" className="text-[#8B95A5] hover:text-[#38BDF8] text-sm">{t('landing.nav.howItWorks')}</a>
                <a href="#sectors" className="text-[#8B95A5] hover:text-[#38BDF8] text-sm">{t('landing.nav.industries')}</a>
              </div>
              <div className="flex gap-6 mb-4">
                <a href="/privacy" className="text-[#8B95A5] hover:text-[#38BDF8] text-sm" data-testid="footer-privacy-link">Privacy Policy</a>
                <a href="/terms" className="text-[#8B95A5] hover:text-[#38BDF8] text-sm" data-testid="footer-terms-link">Terms of Service</a>
                <a href="/security-policy" className="text-[#8B95A5] hover:text-[#38BDF8] text-sm" data-testid="footer-security-link">Security Policy</a>
                <a href="mailto:support@charstan.com" className="text-[#8B95A5] hover:text-[#38BDF8] text-sm" data-testid="footer-support-link">Support / Contact</a>
              </div>
              <a href="mailto:info@charstan.com" className="text-[#38BDF8] hover:underline text-sm" data-testid="footer-email">info@charstan.com</a>
            </div>
          </div>
          <div className="mt-8 pt-6 border-t border-[rgba(255,255,255,0.07)] text-center">
            <p className="text-[#8B95A5] text-xs">&copy; {new Date().getFullYear()} CHARSTAN. {t('landing.footer.allRights')}</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
